import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ParcelOrderService from "../../services/parcel-order.service";
import ParcelService from "../../services/parcel.service";
import ProvinceService from "../../services/province.service";
import TownService from "../../services/town.service";
import AuthService from "../../services/auth.service";
import AddressInput from "../../components/AddressInput";
import {
  HiCheck,
  HiExclamationTriangle,
  HiEnvelope,
  HiOutlineCube,
  HiCube,
  HiTruck,
  HiMinus,
  HiPlus,
  HiArrowPath,
  HiCalendar,
  HiArrowRight,
} from "react-icons/hi2";
import { useSelectedBusiness } from "../../context/BusinessContext";

const STEPS = ["count", "sizes", "address", "review"];

function StepIndicator({ current }) {
  const labels = ["Parcel Count", "Sizes", "Delivery", "Review"];
  return (
    <div className="flex items-center gap-0 mb-8">
      {labels.map((label, i) => {
        const stepIndex = i;
        const currentIndex = STEPS.indexOf(current);
        const done = stepIndex < currentIndex;
        const active = stepIndex === currentIndex;
        return (
          <div key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div
                className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors
                  ${done ? "bg-violet-600 text-white" : active ? "bg-violet-100 text-violet-700 ring-2 ring-violet-400" : "bg-gray-100 text-gray-400"}`}
              >
                {done ? <HiCheck className="h-4 w-4" /> : i + 1}
              </div>
              <span
                className={`text-xs mt-1 font-medium ${active ? "text-violet-700" : done ? "text-violet-500" : "text-gray-400"}`}
              >
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-2 mb-5 rounded ${done ? "bg-violet-400" : "bg-gray-200"}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function SelectField({
  value,
  onChange,
  placeholder,
  options = [],
  loading,
  disabled,
}) {
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled || loading}
      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <option value="">{loading ? "Loading…" : placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export default function BulkUploadPage() {
  const navigate = useNavigate();
  const user = AuthService.getCurrentUser()?.user;

  // Reference data
  const [parcels, setParcels] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [allTowns, setAllTowns] = useState([]);
  const [loadingRef, setLoadingRef] = useState(true);

  const { selectedBusiness: business } = useSelectedBusiness();

  // Wizard state
  const [step, setStep] = useState("count");

  // Step 1
  const [totalCount, setTotalCount] = useState("");

  // Step 2 — sizes: { [parcel_id]: quantity }
  const [sizes, setSizes] = useState({});

  // Step 3 — address
  const [collectionDate, setCollectionDate] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [departureProvince, setDepartureProvince] = useState("");
  const [departureTown, setDepartureTown] = useState("");
  const [sameDestination, setSameDestination] = useState(null); // null | true | false
  const [sharedDest, setSharedDest] = useState({ province: "", town: "" });
  // One entry per individual parcel: [{ parcel_id, province, town }, ...]
  const [destinations, setDestinations] = useState([]);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [parcelsData, provsData, townsData] = await Promise.allSettled([
          ParcelService.index(),
          ProvinceService.index(),
          TownService.index(),
        ]);
        if (parcelsData.status === "fulfilled")
          setParcels(parcelsData.value ?? []);
        if (provsData.status === "fulfilled")
          setProvinces(provsData.value ?? []);
        if (townsData.status === "fulfilled")
          setAllTowns(townsData.value ?? []);
      } finally {
        setLoadingRef(false);
      }
    };
    load();
  }, []);

  // Derived
  const depTowns = allTowns.filter(
    (t) => t.province_id?.toString() === departureProvince,
  );
  const total = parseInt(totalCount, 10) || 0;
  const allocated = Object.values(sizes).reduce(
    (s, v) => s + (parseInt(v, 10) || 0),
    0,
  );
  const remaining = total - allocated;

  const provinceOptions = provinces.map((p) => ({
    value: p.id.toString(),
    label: p.name,
  }));
  const depTownOptions = depTowns.map((t) => ({
    value: t.id.toString(),
    label: t.name,
  }));

  function destTownOptionsFor(province) {
    return allTowns
      .filter((t) => t.province_id?.toString() === province)
      .map((t) => ({ value: t.id.toString(), label: t.name }));
  }

  function setDestField(index, field, value) {
    setDestinations((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: value,
        ...(field === "province" ? { town: "" } : {}),
      };
      return next;
    });
  }

  // Expand sizes into individual parcels and navigate to address step
  function goToAddress() {
    const entries = [];
    parcels.forEach((p) => {
      const qty = parseInt(sizes[p.id], 10) || 0;
      for (let i = 0; i < qty; i++) {
        entries.push({ parcel_id: p.id, province: "", town: "" });
      }
    });
    setDestinations(entries);
    setStep("address");
  }

  // Each destination entry becomes its own order (quantity: 1)
  function buildOrders() {
    return destinations.map((d) => {
      const p = parcels.find((pr) => pr.id === d.parcel_id);
      const destProv = sameDestination ? sharedDest.province : d.province;
      const destTown = sameDestination ? sharedDest.town : d.town;
      return {
        parcel_id: d.parcel_id.toString(),
        quantity: 1,
        price_id: p?.parcel_prices?.[0]?.price?.id?.toString() ?? "",
        pickup_location: pickupLocation,
        collection_date: collectionDate,
        departure_province: departureProvince,
        departure_town: departureTown,
        destination_province: destProv,
        destination_town: destTown,
      };
    });
  }

  async function handleSubmit() {
    if (!business?.id) {
      setSubmitError(
        "No business selected. Please refresh the page and try again.",
      );
      return;
    }
    const orders = buildOrders();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const parcelOrders = orders.map((o) => ({
        user_id: user?.id,
        business_id: business.id,
        parcel_id: o.parcel_id,
        price_id: o.price_id,
        pickup_location: o.pickup_location,
        quantity: o.quantity,
        collection_date: o.collection_date,
        departure_province: o.departure_province,
        destination_province: o.destination_province,
        departure_town: o.departure_town,
        destination_town: o.destination_town,
      }));

      console.log("Submitting bulk order with payload:", parcelOrders);
      const result = await ParcelOrderService.bulkCreate(parcelOrders);

      console.log("Bulk order created successfully:", result);

      if (result.payment_url) {
        window.location.assign(result.payment_url);
      } else {
        setSubmitError("Payment URL not received. Please contact support.");
        setSubmitting(false);
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data?.details) {
        setSubmitError(data.details);
      } else {
        setSubmitError(
          data?.error ?? "Something went wrong. Please try again.",
        );
      }
      setSubmitting(false);
    }
  }

  // ── Step validations ──
  const step1Valid = total >= 1;
  const step2Valid = allocated === total && total > 0;
  const selectedParcels = parcels.filter((p) => parseInt(sizes[p.id], 10) > 0);
  const destsValid =
    sameDestination === true
      ? sharedDest.province && sharedDest.town
      : sameDestination === false &&
        destinations.length > 0 &&
        destinations.every((d) => d.province && d.town);
  const step3Valid =
    collectionDate &&
    pickupLocation.trim() &&
    departureProvince &&
    departureTown &&
    destsValid;

  return (
    <div className="px-4 py-8 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900">Bulk Order</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          Create multiple parcels in a few quick steps
        </p>
      </div>

      <StepIndicator current={step} />

      {/* ── Step 1: Count ── */}
      {step === "count" && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-base font-bold text-gray-800 mb-1">
            How many parcels do you need to send?
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            Enter the total number of parcels for this batch.
          </p>
          <div className="max-w-xs">
            <input
              type="number"
              min="1"
              value={totalCount}
              onChange={(e) => setTotalCount(e.target.value)}
              placeholder="e.g. 10"
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-2xl font-bold text-gray-900 text-center focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
              autoFocus
            />
            {total > 0 && (
              <p className="text-sm text-gray-400 mt-2 text-center">
                {total} parcel{total !== 1 ? "s" : ""}
              </p>
            )}
          </div>
          <div className="flex justify-end mt-8">
            <button
              onClick={() => setStep("sizes")}
              disabled={!step1Valid}
              className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Sizes ── */}
      {step === "sizes" && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-base font-bold text-gray-800 mb-1">
            What sizes are your {total} parcels?
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            Split the total across available parcel sizes. All {total} must be
            accounted for.
          </p>

          {loadingRef ? (
            <p className="text-sm text-gray-400">Loading parcel types…</p>
          ) : parcels.length === 0 ? (
            <p className="text-sm text-red-500">
              Could not load parcel types. Please refresh.
            </p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {parcels.map((p, idx) => {
                const qty = parseInt(sizes[p.id], 10) || 0;
                const selected = qty > 0;
                const price = p.parcel_prices?.[0]?.price;
                const dims = [
                  p.max_height && `${p.max_height}`,
                  p.max_width && `${p.max_width}`,
                  p.max_weight && `${p.max_weight}`,
                ]
                  .filter(Boolean)
                  .join(" × ");

                const palette = [
                  {
                    bg: "bg-violet-500",
                    light: "bg-violet-50",
                    border: "border-violet-400",
                    text: "text-violet-700",
                    badge: "bg-violet-100 text-violet-700",
                  },
                  {
                    bg: "bg-sky-500",
                    light: "bg-sky-50",
                    border: "border-sky-400",
                    text: "text-sky-700",
                    badge: "bg-sky-100 text-sky-700",
                  },
                  {
                    bg: "bg-emerald-500",
                    light: "bg-emerald-50",
                    border: "border-emerald-400",
                    text: "text-emerald-700",
                    badge: "bg-emerald-100 text-emerald-700",
                  },
                  {
                    bg: "bg-amber-500",
                    light: "bg-amber-50",
                    border: "border-amber-400",
                    text: "text-amber-700",
                    badge: "bg-amber-100 text-amber-700",
                  },
                ];
                const theme = palette[idx % palette.length];

                const icons = [
                  /* envelope / small */
                  <HiEnvelope key="a" className="h-5 w-5 text-white" />,
                  /* box */
                  <HiOutlineCube key="b" className="h-5 w-5 text-white" />,
                  /* cube / large */
                  <HiCube key="c" className="h-5 w-5 text-white" />,
                  /* truck / xl */
                  <HiTruck key="d" className="h-5 w-5 text-white" />,
                ];

                return (
                  <div
                    key={p.id}
                    className={`flex items-center gap-0 rounded-2xl border-2 overflow-hidden transition-all shadow-sm ${selected ? `${theme.border} ${theme.light}` : "border-gray-100 bg-white"}`}
                  >
                    {/* Coloured icon strip */}
                    <div
                      className={`${theme.bg} flex flex-col items-center justify-center self-stretch px-3 py-4 flex-shrink-0`}
                    >
                      {icons[idx % icons.length]}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 px-3 py-3">
                      <p
                        className={`text-sm font-extrabold leading-tight ${selected ? theme.text : "text-gray-800"}`}
                      >
                        {p.package_type}
                      </p>
                      {dims && (
                        <p className="text-xs text-gray-400 mt-0.5">{dims}</p>
                      )}
                      {price && (
                        <p
                          className={`text-xs font-bold mt-1 ${selected ? theme.text : "text-gray-400"}`}
                        >
                          From R{price.value}
                        </p>
                      )}
                    </div>

                    {/* Stepper */}
                    <div className="flex items-center gap-1 px-3 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() =>
                          setSizes((prev) => ({
                            ...prev,
                            [p.id]: Math.max(0, qty - 1),
                          }))
                        }
                        disabled={qty === 0}
                        className="h-7 w-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors bg-white"
                      >
                        <HiMinus className="h-3 w-3" />
                      </button>
                      <input
                        type="number"
                        min="0"
                        max={total}
                        value={sizes[p.id] ?? ""}
                        onChange={(e) =>
                          setSizes((prev) => ({
                            ...prev,
                            [p.id]: e.target.value,
                          }))
                        }
                        placeholder="0"
                        className="w-10 px-1 py-1 text-center border border-gray-200 rounded-lg text-sm font-bold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setSizes((prev) => ({ ...prev, [p.id]: qty + 1 }))
                        }
                        disabled={allocated >= total}
                        className="h-7 w-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors bg-white"
                      >
                        <HiPlus className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Running total */}
          <div
            className={`mt-5 p-3 rounded-xl flex items-center justify-between text-sm font-semibold
            ${allocated === total && total > 0 ? "bg-green-50 text-green-700" : allocated > total ? "bg-red-50 text-red-600" : "bg-gray-50 text-gray-500"}`}
          >
            <span>Allocated</span>
            <span>
              {allocated} / {total}
            </span>
          </div>
          {allocated > total && (
            <p className="text-xs text-red-500 mt-1">
              Total exceeds {total}. Please adjust the quantities.
            </p>
          )}
          {remaining > 0 && allocated < total && (
            <p className="text-xs text-amber-600 mt-1">
              {remaining} parcel{remaining !== 1 ? "s" : ""} still unallocated.
            </p>
          )}

          <div className="flex justify-between mt-8">
            <button
              onClick={() => setStep("count")}
              className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={goToAddress}
              disabled={!step2Valid}
              className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Address ── */}
      {step === "address" && (
        <div className="space-y-5">
          {/* Collection & pickup */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-bold text-gray-800 mb-5">
              Collection details
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Collection Date" required>
                <input
                  type="date"
                  value={collectionDate}
                  onChange={(e) => setCollectionDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
                />
              </Field>

              <Field label="From Province" required>
                <SelectField
                  value={departureProvince}
                  onChange={(e) => {
                    setDepartureProvince(e.target.value);
                    setDepartureTown("");
                  }}
                  placeholder="Select province"
                  options={provinceOptions}
                  loading={loadingRef}
                />
              </Field>
              <Field label="From Town" required>
                <SelectField
                  value={departureTown}
                  onChange={(e) => setDepartureTown(e.target.value)}
                  placeholder={
                    departureProvince ? "Select town" : "Select province first"
                  }
                  options={depTownOptions}
                  disabled={!departureProvince}
                />
              </Field>
              <div className="sm:col-span-2">
                <AddressInput
                  id="pickupLocation"
                  name="pickupLocation"
                  label="Pickup Address"
                  showRequired
                  value={pickupLocation}
                  onChange={(e) => setPickupLocation(e.target.value)}
                  placeholder="e.g. 123 Main Street, Cape Town"
                  touched={true}
                />
              </div>
            </div>
          </div>

          {/* Same destination question */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-bold text-gray-800 mb-1">
              Are all {total} parcels going to the same place?
            </h2>
            <p className="text-sm text-gray-400 mb-5">
              If yes, enter the destination once. If not, you'll set each
              parcel's destination individually.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <button
                type="button"
                onClick={() => setSameDestination(true)}
                className={`p-4 rounded-xl border-2 text-sm font-semibold transition-colors text-left
                  ${sameDestination === true ? "border-violet-500 bg-violet-50 text-violet-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
              >
                <div className="text-base mb-0.5">Yes</div>
                <div className="text-xs font-normal text-gray-400">
                  Same destination for all
                </div>
              </button>
              <button
                type="button"
                onClick={() => setSameDestination(false)}
                className={`p-4 rounded-xl border-2 text-sm font-semibold transition-colors text-left
                  ${sameDestination === false ? "border-violet-500 bg-violet-50 text-violet-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
              >
                <div className="text-base mb-0.5">No</div>
                <div className="text-xs font-normal text-gray-400">
                  Different destination per parcel
                </div>
              </button>
            </div>

            {/* Shared destination */}
            {sameDestination === true && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="To Province" required>
                  <SelectField
                    value={sharedDest.province}
                    onChange={(e) =>
                      setSharedDest({ province: e.target.value, town: "" })
                    }
                    placeholder="Select province"
                    options={provinceOptions}
                    loading={loadingRef}
                  />
                </Field>
                <Field label="To Town" required>
                  <SelectField
                    value={sharedDest.town}
                    onChange={(e) =>
                      setSharedDest((prev) => ({
                        ...prev,
                        town: e.target.value,
                      }))
                    }
                    placeholder={
                      sharedDest.province
                        ? "Select town"
                        : "Select province first"
                    }
                    options={destTownOptionsFor(sharedDest.province)}
                    disabled={!sharedDest.province}
                  />
                </Field>
              </div>
            )}
          </div>

          {/* Per-individual-parcel destinations */}
          {sameDestination === false && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-base font-bold text-gray-800 mb-1">
                Destinations
              </h2>
              <p className="text-sm text-gray-400 mb-5">
                Each parcel needs its own delivery destination.
              </p>
              <div className="space-y-3">
                {destinations.map((d, idx) => {
                  const parcel = parcels.find((p) => p.id === d.parcel_id);
                  // Count how many of this type appeared before this index to get #1, #2 etc.
                  const numBefore = destinations
                    .slice(0, idx)
                    .filter((x) => x.parcel_id === d.parcel_id).length;
                  const palette = [
                    {
                      bg: "bg-violet-500",
                      border: "border-violet-200",
                      light: "bg-violet-50",
                      text: "text-violet-700",
                    },
                    {
                      bg: "bg-sky-500",
                      border: "border-sky-200",
                      light: "bg-sky-50",
                      text: "text-sky-700",
                    },
                    {
                      bg: "bg-emerald-500",
                      border: "border-emerald-200",
                      light: "bg-emerald-50",
                      text: "text-emerald-700",
                    },
                    {
                      bg: "bg-amber-500",
                      border: "border-amber-200",
                      light: "bg-amber-50",
                      text: "text-amber-700",
                    },
                  ];
                  const typeIdx = selectedParcels.findIndex(
                    (p) => p.id === d.parcel_id,
                  );
                  const theme = palette[typeIdx % palette.length];
                  return (
                    <div
                      key={idx}
                      className={`rounded-xl border-2 ${d.province && d.town ? theme.border : "border-gray-200"} ${d.province && d.town ? theme.light : "bg-gray-50"} p-4`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className={`h-6 w-6 rounded-lg ${theme.bg} flex items-center justify-center flex-shrink-0`}
                        >
                          <HiOutlineCube className="h-3.5 w-3.5 text-white" />
                        </div>
                        <p className={`text-sm font-bold ${theme.text}`}>
                          {parcel?.package_type}
                        </p>
                        <span className="ml-auto text-xs font-semibold text-gray-400">
                          #{numBefore + 1}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="To Province" required>
                          <SelectField
                            value={d.province}
                            onChange={(e) =>
                              setDestField(idx, "province", e.target.value)
                            }
                            placeholder="Select province"
                            options={provinceOptions}
                            loading={loadingRef}
                          />
                        </Field>
                        <Field label="To Town" required>
                          <SelectField
                            value={d.town}
                            onChange={(e) =>
                              setDestField(idx, "town", e.target.value)
                            }
                            placeholder={
                              d.province
                                ? "Select town"
                                : "Select province first"
                            }
                            options={destTownOptionsFor(d.province)}
                            disabled={!d.province}
                          />
                        </Field>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <button
              onClick={() => setStep("sizes")}
              className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => setStep("review")}
              disabled={!step3Valid}
              className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors"
            >
              Review Order
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4: Review ── */}
      {step === "review" && (
        <div className="space-y-4">
          {/* Parcels summary */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center">
                <HiOutlineCube className="h-4 w-4 text-violet-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-800">Parcels</h3>
              <span className="ml-auto text-xs font-bold text-violet-600 bg-violet-50 px-2.5 py-1 rounded-full">
                {total} total
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {parcels
                .filter((p) => parseInt(sizes[p.id], 10) > 0)
                .map((p, idx) => {
                  const palette = [
                    {
                      bg: "bg-violet-500",
                      badge: "bg-violet-100 text-violet-700",
                    },
                    { bg: "bg-sky-500", badge: "bg-sky-100 text-sky-700" },
                    {
                      bg: "bg-emerald-500",
                      badge: "bg-emerald-100 text-emerald-700",
                    },
                    {
                      bg: "bg-amber-500",
                      badge: "bg-amber-100 text-amber-700",
                    },
                  ];
                  const icons = [
                    <HiEnvelope key="a" className="h-4 w-4 text-white" />,
                    <HiOutlineCube key="b" className="h-4 w-4 text-white" />,
                    <HiCube key="c" className="h-4 w-4 text-white" />,
                    <HiTruck key="d" className="h-4 w-4 text-white" />,
                  ];
                  const theme = palette[idx % palette.length];
                  return (
                    <div
                      key={p.id}
                      className="flex items-center rounded-xl overflow-hidden border border-gray-100 shadow-sm"
                    >
                      <div
                        className={`${theme.bg} flex items-center justify-center self-stretch px-2.5`}
                      >
                        {icons[idx % icons.length]}
                      </div>
                      <div className="flex-1 px-3 py-2.5 flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-700">
                          {p.package_type}
                        </span>
                        <span
                          className={`text-xs font-bold px-2 py-0.5 rounded-full ${theme.badge}`}
                        >
                          × {sizes[p.id]}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Collection details */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center">
                <HiCalendar className="h-4 w-4 text-violet-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-800">Collection</h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-xs text-gray-400 uppercase tracking-wide font-semibold">
                  Date
                </span>
                <span className="text-sm font-semibold text-gray-800">
                  {collectionDate}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-400 uppercase tracking-wide font-semibold">
                  Pickup address
                </span>
                <span className="text-sm font-semibold text-gray-800 text-right max-w-[55%]">
                  {pickupLocation}
                </span>
              </div>
            </div>
          </div>

          {/* Routes */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center">
                <HiArrowRight className="h-4 w-4 text-violet-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-800">Routes</h3>
            </div>
            {sameDestination ? (
              // Single shared route
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 font-semibold mb-1">
                    From
                  </p>
                  <p className="text-sm font-bold text-gray-800">
                    {provinces.find(
                      (pr) => pr.id.toString() === departureProvince,
                    )?.name ?? "—"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {allTowns.find((t) => t.id.toString() === departureTown)
                      ?.name ?? "—"}
                  </p>
                </div>
                <HiArrowRight className="h-5 w-5 text-violet-400 flex-shrink-0" />
                <div className="flex-1 bg-violet-50 rounded-xl p-3">
                  <p className="text-xs text-violet-400 font-semibold mb-1">
                    To (all {total})
                  </p>
                  <p className="text-sm font-bold text-violet-800">
                    {provinces.find(
                      (pr) => pr.id.toString() === sharedDest.province,
                    )?.name ?? "—"}
                  </p>
                  <p className="text-xs text-violet-600">
                    {allTowns.find((t) => t.id.toString() === sharedDest.town)
                      ?.name ?? "—"}
                  </p>
                </div>
              </div>
            ) : (
              // Per-parcel routes
              <div className="space-y-3">
                {destinations.map((d, idx) => {
                  const parcel = parcels.find((p) => p.id === d.parcel_id);
                  const numBefore = destinations
                    .slice(0, idx)
                    .filter((x) => x.parcel_id === d.parcel_id).length;
                  const palette = [
                    { bg: "bg-violet-500" },
                    { bg: "bg-sky-500" },
                    { bg: "bg-emerald-500" },
                    { bg: "bg-amber-500" },
                  ];
                  const typeIdx = selectedParcels.findIndex(
                    (p) => p.id === d.parcel_id,
                  );
                  const theme = palette[typeIdx % palette.length];
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div
                        className={`${theme.bg} text-white text-xs font-bold px-2.5 py-1.5 rounded-lg flex-shrink-0 text-center min-w-[4rem]`}
                      >
                        <div>{parcel?.package_type}</div>
                        <div className="font-normal opacity-75">
                          #{numBefore + 1}
                        </div>
                      </div>
                      <div className="flex-1 flex items-center gap-2 min-w-0">
                        <div className="flex-1 bg-gray-50 rounded-xl p-2.5 min-w-0">
                          <p className="text-xs text-gray-400 font-semibold">
                            From
                          </p>
                          <p className="text-xs font-bold text-gray-800 truncate">
                            {provinces.find(
                              (pr) => pr.id.toString() === departureProvince,
                            )?.name ?? "—"}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {allTowns.find(
                              (t) => t.id.toString() === departureTown,
                            )?.name ?? "—"}
                          </p>
                        </div>
                        <HiArrowRight className="h-4 w-4 text-violet-400 flex-shrink-0" />
                        <div className="flex-1 bg-gray-50 rounded-xl p-2.5 min-w-0">
                          <p className="text-xs text-gray-400 font-semibold">
                            To
                          </p>
                          <p className="text-xs font-bold text-gray-800 truncate">
                            {provinces.find(
                              (pr) => pr.id.toString() === d.province,
                            )?.name ?? "—"}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {allTowns.find((t) => t.id.toString() === d.town)
                              ?.name ?? "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Validation errors from bulk API */}
          {submitError && (
            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <HiExclamationTriangle className="text-red-500 mt-0.5 flex-shrink-0 h-5 w-5" />
                <div>
                  {typeof submitError === "string" ? (
                    <p className="text-sm text-red-700">{submitError}</p>
                  ) : (
                    <>
                      <p className="font-bold text-red-800 text-sm mb-1">
                        Some orders failed validation
                      </p>
                      <ul className="text-sm text-red-700 list-disc list-inside space-y-0.5">
                        {submitError.map((detail, i) => (
                          <li key={i}>
                            Order #{detail.index + 1}:{" "}
                            {detail.errors.join(", ")}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <button
              onClick={() => setStep("address")}
              disabled={submitting}
              className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors"
            >
              {submitting ? (
                <>
                  <HiArrowPath className="h-4 w-4 animate-spin" />
                  Redirecting to payment…
                </>
              ) : (
                `Pay for ${total} Parcel${total !== 1 ? "s" : ""}`
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
